import { lazy, Suspense } from 'react';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { HeroPoster } from './HeroPoster';
import { HeroReducedMotion } from './HeroReducedMotion';

const HeroPin = lazy(() =>
  import('./HeroPin').then((m) => ({ default: m.HeroPin }))
);

/**
 * The single decision point for the hero branch.
 *
 * - cinematic: 3-segment Veo MP4 scrub (v1.0). No WebGL, no GPU class needed —
 *   modern mobile browsers handle 1080p H.264 currentTime scrub.
 * - reduced:   3 keyframe stills with IO crossfades (WCAG 2.3.3)
 * - poster:    real Veo segment-01 frame 0 — only used as the Suspense
 *   fallback while HeroPin's chunk loads; no longer a device-gated branch.
 *
 * v1.0.3 removed the `useDeviceClass()` routing. The prior heuristic
 * (cores + memory + hover − isMobile) classified every iPhone as `'low'`
 * because Safari doesn't report `navigator.deviceMemory`, defaulting it
 * to the 4-GB midpoint, then docking 1 for `isMobile` — net score 1,
 * which is below the `'mid'` threshold. That routed every mobile user
 * to the legacy stylized-panels HeroPoster instead of the new cinematic.
 */
export function Hero() {
  const reducedMotion = useReducedMotion();

  if (reducedMotion) {
    return <HeroReducedMotion />;
  }

  return (
    <Suspense fallback={<HeroPoster />}>
      <HeroPin />
    </Suspense>
  );
}
